import { Play } from 'lucide-react'
import type { ReactNode } from 'react'
import { plural } from '../../lib/format'
import { InvitePanel } from '../InvitePanel'
import { type ArcadeTone, Button } from '../ui'
import { Lineup, type LineupEntry } from './Lineup'
import { hostNameOf, WaitingPill } from './WaitingPill'

interface Props {
  eyebrow: string
  title: string
  entries: LineupEntry[]
  min: number
  max: number
  figure: (entry: LineupEntry) => ReactNode
  hostId: string
  playerId: string | null
  onStart: () => void
  startLabel: string
  startTone?: ArcadeTone
  onJoin: () => void
  joinLabel: string
  fullLabel: string
  /** What one more seat is called in "Waiting for 2 more racers". */
  seatNoun: string
  invite: { code: string; path: string; game: 'ice' | 'cycles' }
  /** Game-specific settings shown under the call to action (level stepper, round count…). */
  children?: ReactNode
}

/** Everything a player needs before a party game starts, in the card over the board. */
export function PartyLobby({
  eyebrow,
  title,
  entries,
  min,
  max,
  figure,
  hostId,
  playerId,
  onStart,
  startLabel,
  startTone = 'go',
  onJoin,
  joinLabel,
  fullLabel,
  seatNoun,
  invite,
  children,
}: Props) {
  const mine = entries.some((e) => e.id === playerId)
  const isHost = hostId === playerId
  const full = entries.length >= max
  const short = Math.max(0, min - entries.length)

  return (
    <div className="flex flex-col items-center gap-2.5">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-widest text-sky-600 dark:text-sky-400">
          {eyebrow}
        </div>
        <h2 className="text-xl font-black leading-tight">{title}</h2>
      </div>

      <Lineup entries={entries} min={min} max={max} figure={figure} />

      {isHost ? (
        <div className="flex w-full flex-col items-center gap-1.5">
          <Button
            variant="arcade"
            tone={startTone}
            className="w-full"
            onClick={onStart}
            disabled={short > 0}
          >
            <Play className="size-5" aria-hidden /> {startLabel}
          </Button>
          {short > 0 && (
            <p className="text-xs text-slate-500">
              Waiting for {plural(short, `more ${seatNoun}`)} to join
            </p>
          )}
        </div>
      ) : mine ? (
        <WaitingPill>{`Waiting for ${hostNameOf(entries, hostId)} to start…`}</WaitingPill>
      ) : full ? (
        <WaitingPill>{fullLabel}</WaitingPill>
      ) : (
        <Button variant="arcade" tone="join" className="w-full" onClick={onJoin}>
          {joinLabel}
        </Button>
      )}

      {children}

      {!full && (
        <div className="w-full">
          <InvitePanel code={invite.code} compact path={invite.path} game={invite.game} />
        </div>
      )}
    </div>
  )
}
